// ============================================
// FAQ 知识中心 — Zustand Store
// ============================================

import { create } from 'zustand';
import { supabase } from '../config/supabase';
import { faqTagIds } from './faq-tag-utils';
import type { FaqItem, FaqItemInput, FaqItemTagOrder, FaqSpace, FaqTag } from './types';

function normalizeFaqItem(row: any): FaqItem {
  return {
    ...row,
    space_id: row.space_id,
    primary_tags: Array.isArray(row.primary_tags) ? row.primary_tags : [],
    secondary_tags: Array.isArray(row.secondary_tags) ? row.secondary_tags : [],
    primary_tag_ids: Array.isArray(row.primary_tag_ids) ? row.primary_tag_ids : [],
    secondary_tag_ids: Array.isArray(row.secondary_tag_ids) ? row.secondary_tag_ids : [],
    attachments: Array.isArray(row.attachments) ? row.attachments : [],
    answer_format: row.answer_format === 'html' ? 'html' : 'tiptap',
    visibility_scope: row.visibility_scope === 'internal' ? 'internal' : 'all',
  } as FaqItem;
}

function normalizeTagOrder(row: any): FaqItemTagOrder {
  return {
    ...row,
    space_id: row.space_id,
    sort_order: typeof row.sort_order === 'number' ? row.sort_order : 0,
  } as FaqItemTagOrder;
}

function requireSpaceId(get: () => FaqState) {
  const spaceId = get().space?.id;
  if (!spaceId) throw new Error('FAQ 空间尚未初始化');
  return spaceId;
}

function globalSortItems(items: FaqItem[]): FaqItem[] {
  return [...items].sort((a, b) => {
    const orderDelta = (a.sort_order ?? 0) - (b.sort_order ?? 0);
    if (orderDelta !== 0) return orderDelta;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
}

function compareByGlobalOrder(a: FaqItem, b: FaqItem): number {
  const orderDelta = (a.sort_order ?? 0) - (b.sort_order ?? 0);
  if (orderDelta !== 0) return orderDelta;
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
}

function sortItemsForTag(items: FaqItem[], tagId: string, tagOrders: FaqItemTagOrder[]): FaqItem[] {
  const orderMap = new Map(
    tagOrders
      .filter((order) => order.tag_id === tagId)
      .map((order) => [order.faq_id, order.sort_order]),
  );

  return [...items].sort((a, b) => {
    const aOrder = orderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
    const bOrder = orderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;

    return compareByGlobalOrder(a, b);
  });
}

function sortItemsForAllView(items: FaqItem[], tags: FaqTag[], tagOrders: FaqItemTagOrder[]): FaqItem[] {
  const orderMap = new Map(
    tagOrders.map((order) => [`${order.tag_id}:${order.faq_id}`, order.sort_order]),
  );

  const itemTagOrder = (item: FaqItem) => {
    const primaryIds = faqTagIds(item, tags, 1);
    const secondaryIds = faqTagIds(item, tags, 2);
    const orderedTagIds = [...primaryIds, ...secondaryIds];

    for (const tagId of orderedTagIds) {
      const order = orderMap.get(`${tagId}:${item.id}`);
      if (typeof order === 'number') return order;
    }

    return Number.MAX_SAFE_INTEGER;
  };

  return [...items].sort((a, b) => {
    const aOrder = itemTagOrder(a);
    const bOrder = itemTagOrder(b);
    if (aOrder !== bOrder) return aOrder - bOrder;

    return compareByGlobalOrder(a, b);
  });
}

function mergeTagOrders(
  existing: FaqItemTagOrder[],
  incoming: FaqItemTagOrder[],
  tagId: string,
): FaqItemTagOrder[] {
  const incomingFaqIds = new Set(incoming.map((order) => order.faq_id));
  return [
    ...existing.filter((order) => !(order.tag_id === tagId && incomingFaqIds.has(order.faq_id))),
    ...incoming,
  ];
}

interface FaqState {
  // 当前 FAQ 空间
  space: FaqSpace | null;

  // 数据
  items: FaqItem[];
  tags: FaqTag[];
  tagOrders: FaqItemTagOrder[];

  // 状态
  loading: boolean;
  error: string | null;
  realtimeToast: string | null;

  // 回收站
  trashItems: FaqItem[];
  trashLoading: boolean;

  // 筛选
  searchQuery: string;
  primaryTagFilter: string | null;
  secondaryTagFilter: string | null;

  // 展开控制
  expandedIds: Set<string>;

  // Actions
  setSpace: (space: FaqSpace | null) => void;
  fetchItems: () => Promise<void>;
  fetchTags: () => Promise<void>;
  fetchTagOrders: () => Promise<void>;
  createItem: (input: FaqItemInput) => Promise<FaqItem>;
  updateItem: (id: string, input: Partial<FaqItemInput>) => Promise<FaqItem>;
  reorderItemsInTag: (tagId: string, orderedFaqIds: string[]) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
  fetchTrashItems: () => Promise<void>;
  restoreItem: (id: string) => Promise<void>;
  hardDeleteItem: (id: string) => Promise<void>;
  createTag: (name: string, level: 1 | 2, name_en?: string) => Promise<void>;
  updateTag: (id: string, updates: { name?: string; name_en?: string }) => Promise<void>;
  deleteTag: (id: string) => Promise<void>;
  incrementViewCount: (id: string) => Promise<void>;

  setSearchQuery: (q: string) => void;
  setPrimaryTagFilter: (tag: string | null) => void;
  setSecondaryTagFilter: (tag: string | null) => void;
  toggleExpanded: (id: string) => void;
  expandAll: () => void;
  collapseAll: () => void;

  // 计算属性
  getFilteredItems: () => FaqItem[];
  getPrimaryTags: () => FaqTag[];
  getSecondaryTags: () => FaqTag[];

  // 实时订阅
  subscribeRealtime: () => void;
  unsubscribeRealtime: () => void;
  dismissToast: () => void;
}

export const useFaqStore = create<FaqState>((set, get) => ({
  space: null,
  items: [],
  tags: [],
  tagOrders: [],
  loading: false,
  error: null,
  realtimeToast: null,
  trashItems: [],
  trashLoading: false,
  searchQuery: '',
  primaryTagFilter: null,
  secondaryTagFilter: null,
  expandedIds: new Set(),

  setSpace: (space) => {
    set((state) => {
      if (state.space?.id === space?.id) return { space };
      return {
        space,
        items: [],
        tags: [],
        tagOrders: [],
        trashItems: [],
        loading: false,
        error: null,
        trashLoading: false,
        searchQuery: '',
        primaryTagFilter: null,
        secondaryTagFilter: null,
        expandedIds: new Set(),
        realtimeToast: null,
      };
    });
  },

  fetchItems: async () => {
    set({ loading: true, error: null });
    try {
      const spaceId = requireSpaceId(get);
      const { data, error } = await supabase
        .from('faq_items')
        .select('*')
        .eq('space_id', spaceId)
        .is('deleted_at', null)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ items: (data ?? []).map(normalizeFaqItem), loading: false });
    } catch (e: any) {
      set({ error: e.message, loading: false });
    }
  },

  fetchTags: async () => {
    try {
      const spaceId = requireSpaceId(get);
      const { data, error } = await supabase
        .from('faq_tags')
        .select('*')
        .eq('space_id', spaceId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      set({ tags: data ?? [] });
    } catch (e: any) {
      console.error('fetchTags failed:', e);
    }
  },

  fetchTagOrders: async () => {
    try {
      const spaceId = requireSpaceId(get);
      const { data, error } = await supabase
        .from('faq_item_tag_orders')
        .select('*')
        .eq('space_id', spaceId)
        .order('tag_id', { ascending: true })
        .order('sort_order', { ascending: true });

      if (error) throw error;
      set({ tagOrders: (data ?? []).map(normalizeTagOrder) });
    } catch (e: any) {
      console.error('fetchTagOrders failed:', e);
    }
  },

  createItem: async (input: FaqItemInput) => {
    const spaceId = requireSpaceId(get);
    const { data: { user } } = await supabase.auth.getUser();
    const payload = {
      ...input,
      space_id: spaceId,
      answer_format: input.answer_format ?? 'tiptap',
      is_published: input.is_published ?? true,
      visibility_scope: input.visibility_scope ?? 'all',
      created_by: user?.id,
    };

    const { data, error } = await supabase
      .from('faq_items')
      .insert(payload)
      .select()
      .single();

    if (error) throw error;
    const item = normalizeFaqItem(data);
    set((s) => ({ items: [item, ...s.items.filter((existing) => existing.id !== item.id)] }));
    return item;
  },

  updateItem: async (id: string, input: Partial<FaqItemInput>) => {
    const spaceId = requireSpaceId(get);
    let previousItem: FaqItem | undefined;
    set((s) => {
      previousItem = s.items.find((item) => item.id === id);
      if (!previousItem) return s;

      return {
        items: s.items.map((item) =>
          item.id === id
            ? { ...item, ...input, updated_at: new Date().toISOString() } as FaqItem
            : item
        ),
      };
    });

    const { data, error } = await supabase
      .from('faq_items')
      .update(input)
      .eq('id', id)
      .eq('space_id', spaceId)
      .select()
      .single();

    if (error) {
      if (previousItem) {
        set((s) => ({
          items: s.items.map((item) => (item.id === id ? previousItem as FaqItem : item)),
        }));
      }
      throw error;
    }
    const item = normalizeFaqItem(data);
    set((s) => ({
      items: s.items.map((existing) => (existing.id === id ? item : existing)),
    }));
    return item;
  },

  reorderItemsInTag: async (tagId: string, orderedFaqIds: string[]) => {
    if (!tagId || orderedFaqIds.length === 0) return;

    const spaceId = requireSpaceId(get);
    const previousOrders = get().tagOrders;
    const now = new Date().toISOString();
    const rows = orderedFaqIds.map((faqId, index) => {
      const existing = previousOrders.find((order) => order.tag_id === tagId && order.faq_id === faqId);
      return {
        id: existing?.id ?? `optimistic-${tagId}-${faqId}`,
        space_id: spaceId,
        tag_id: tagId,
        faq_id: faqId,
        sort_order: (index + 1) * 10,
        created_at: existing?.created_at ?? now,
        updated_at: now,
      } satisfies FaqItemTagOrder;
    });

    set((s) => ({
      tagOrders: mergeTagOrders(s.tagOrders, rows, tagId),
    }));

    const { data, error } = await supabase
      .from('faq_item_tag_orders')
      .upsert(
        rows.map((row) => ({
          space_id: row.space_id,
          tag_id: row.tag_id,
          faq_id: row.faq_id,
          sort_order: row.sort_order,
        })),
        { onConflict: 'tag_id,faq_id' },
      )
      .select();

    if (error) {
      set({ tagOrders: previousOrders });
      throw error;
    }

    const authoritativeRows = (data ?? []).map(normalizeTagOrder);
    set((s) => ({
      tagOrders: mergeTagOrders(s.tagOrders, authoritativeRows, tagId),
    }));
  },

  deleteItem: async (id: string) => {
    const spaceId = requireSpaceId(get);
    const { error } = await supabase
      .from('faq_items')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id)
      .eq('space_id', spaceId);

    if (error) throw error;
    set((s) => ({ items: s.items.filter((item) => item.id !== id) }));
  },

  fetchTrashItems: async () => {
    set({ trashLoading: true });
    try {
      const spaceId = requireSpaceId(get);
      const { data, error } = await supabase
        .from('faq_items')
        .select('*')
        .eq('space_id', spaceId)
        .not('deleted_at', 'is', null)
        .order('deleted_at', { ascending: false });

      if (error) throw error;
      set({ trashItems: (data ?? []).map(normalizeFaqItem), trashLoading: false });
    } catch (e: any) {
      set({ trashLoading: false });
      console.error('fetchTrashItems failed:', e);
    }
  },

  restoreItem: async (id: string) => {
    const spaceId = requireSpaceId(get);
    const { data, error } = await supabase
      .from('faq_items')
      .update({ deleted_at: null })
      .eq('id', id)
      .eq('space_id', spaceId)
      .select()
      .single();

    if (error) throw error;
    set((s) => ({
      trashItems: s.trashItems.filter((item) => item.id !== id),
      items: [normalizeFaqItem(data), ...s.items],
    }));
  },

  hardDeleteItem: async (id: string) => {
    const spaceId = requireSpaceId(get);
    const { error } = await supabase
      .from('faq_items')
      .delete()
      .eq('id', id)
      .eq('space_id', spaceId);

    if (error) throw error;
    set((s) => ({ trashItems: s.trashItems.filter((item) => item.id !== id) }));
  },

  createTag: async (name: string, level: 1 | 2, name_en?: string) => {
    const spaceId = requireSpaceId(get);
    const maxOrder = get().tags
      .filter((t) => t.level === level)
      .reduce((max, t) => Math.max(max, t.sort_order), 0);

    const payload: any = { space_id: spaceId, name, level, sort_order: maxOrder + 1 };
    if (name_en) payload.name_en = name_en;

    const { data, error } = await supabase
      .from('faq_tags')
      .insert(payload)
      .select()
      .single();

    if (error) throw error;
    set((s) => ({ tags: [...s.tags, data as FaqTag] }));
  },

  updateTag: async (id: string, updates: { name?: string; name_en?: string }) => {
    const spaceId = requireSpaceId(get);
    const { data, error } = await supabase
      .from('faq_tags')
      .update(updates)
      .eq('id', id)
      .eq('space_id', spaceId)
      .select()
      .single();

    if (error) throw error;
    set((s) => ({
      tags: s.tags.map((t) => (t.id === id ? (data as FaqTag) : t)),
    }));
  },

  deleteTag: async (id: string) => {
    const spaceId = requireSpaceId(get);
    const { error } = await supabase
      .from('faq_tags')
      .delete()
      .eq('id', id)
      .eq('space_id', spaceId);

    if (error) throw error;
    set((s) => ({ tags: s.tags.filter((t) => t.id !== id) }));
  },

  setSearchQuery: (q) => set({ searchQuery: q }),
  setPrimaryTagFilter: (tag) => set({ primaryTagFilter: tag }),
  setSecondaryTagFilter: (tag) => set({ secondaryTagFilter: tag }),

  toggleExpanded: (id) => {
    set((s) => {
      const next = new Set(s.expandedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { expandedIds: next };
    });
  },

  expandAll: () => {
    set((s) => ({
      expandedIds: new Set(s.items.map((i) => i.id)),
    }));
  },

  collapseAll: () => set({ expandedIds: new Set() }),

  incrementViewCount: async (id: string) => {
    try {
      const spaceId = requireSpaceId(get);
      await supabase.rpc('increment_faq_view_count', { faq_id: id, faq_space_id: spaceId });
      // 乐观更新本地状态
      set((s) => ({
        items: s.items.map((item) =>
          item.id === id ? { ...item, view_count: item.view_count + 1 } : item
        ),
      }));
    } catch (e) {
      console.error('Increment view count failed:', e);
    }
  },

  getFilteredItems: () => {
    const { items, tags, tagOrders, searchQuery, primaryTagFilter, secondaryTagFilter } = get();
    let filtered = items;
    const activeSortTagId = secondaryTagFilter ?? primaryTagFilter;

    if (primaryTagFilter) {
      filtered = filtered.filter((i) => faqTagIds(i, tags, 1).includes(primaryTagFilter));
    }
    if (secondaryTagFilter) {
      filtered = filtered.filter((i) => faqTagIds(i, tags, 2).includes(secondaryTagFilter));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((i) =>
        i.question.toLowerCase().includes(q) ||
        (i.answer && i.answer.toLowerCase().includes(q)) ||
        (i.notes && i.notes.toLowerCase().includes(q))
      );
    }

    if (activeSortTagId) {
      return sortItemsForTag(filtered, activeSortTagId, tagOrders);
    }

    return tagOrders.length > 0
      ? sortItemsForAllView(filtered, tags, tagOrders)
      : globalSortItems(filtered);
  },

  getPrimaryTags: () => get().tags.filter((t) => t.level === 1),
  getSecondaryTags: () => get().tags.filter((t) => t.level === 2),

  // ── Supabase Realtime 订阅 ──
  subscribeRealtime: () => {
    const space = get().space;
    if (!space) return;

    const channelKey = `faq:${space.id}`;
    const globalWindow = window as any;
    globalWindow.__faqRealtimeChannels = globalWindow.__faqRealtimeChannels ?? {};
    if (globalWindow.__faqRealtimeChannels[channelKey]) return;

    const isCurrentSpaceRow = (row: any) => row?.space_id === space.id;

    const channel = supabase
      .channel(`faq-realtime-sync-${space.slug}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'faq_items' },
        (payload) => {
          const { eventType, new: newRow, old: oldRow } = payload;
          if (!isCurrentSpaceRow(newRow) && !isCurrentSpaceRow(oldRow)) return;

          if (eventType === 'INSERT' && newRow) {
            set((s) => {
              if (s.items.some((i) => i.id === newRow.id)) return s;
              return {
                items: [normalizeFaqItem(newRow), ...s.items],
                realtimeToast: `新 FAQ 已发布，已同步`,
              };
            });
          } else if (eventType === 'UPDATE' && newRow) {
            set((s) => {
              // 如果本地数据和远程一样则跳过（自己保存触发的）
              const incomingItem = normalizeFaqItem(newRow);
              if (incomingItem.deleted_at) {
                return {
                  items: s.items.filter((item) => item.id !== incomingItem.id),
                  realtimeToast: `一条 FAQ 已移入回收站`,
                };
              }
              const local = s.items.find((i) => i.id === newRow.id);
              if (local && local.updated_at === incomingItem.updated_at) return s;
              return {
                items: s.items.map((item) =>
                  item.id === newRow.id ? incomingItem : item
                ),
                realtimeToast: `「${incomingItem.question?.slice(0, 20) || 'FAQ'}」已更新`,
              };
            });
          } else if (eventType === 'DELETE' && oldRow) {
            set((s) => ({
              items: s.items.filter((item) => item.id !== oldRow.id),
              realtimeToast: `一条 FAQ 已被删除`,
            }));
          }
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'faq_tags' },
        (payload) => {
          const { new: newRow, old: oldRow } = payload;
          if (!isCurrentSpaceRow(newRow) && !isCurrentSpaceRow(oldRow)) return;
          // 标签变更：直接重新拉取（标签数量少）
          get().fetchTags();
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'faq_item_tag_orders' },
        (payload) => {
          const { eventType, new: newRow, old: oldRow } = payload;
          if (!isCurrentSpaceRow(newRow) && !isCurrentSpaceRow(oldRow)) return;

          if ((eventType === 'INSERT' || eventType === 'UPDATE') && newRow) {
            const incomingOrder = normalizeTagOrder(newRow);
            set((s) => ({
              tagOrders: mergeTagOrders(s.tagOrders, [incomingOrder], incomingOrder.tag_id),
            }));
          } else if (eventType === 'DELETE' && oldRow) {
            set((s) => ({
              tagOrders: s.tagOrders.filter((order) => {
                if (oldRow.id) return order.id !== oldRow.id;
                return !(order.tag_id === oldRow.tag_id && order.faq_id === oldRow.faq_id);
              }),
            }));
          }
        },
      )
      .subscribe();

    globalWindow.__faqRealtimeChannels[channelKey] = channel;
  },

  unsubscribeRealtime: () => {
    const globalWindow = window as any;
    const channels = globalWindow.__faqRealtimeChannels;
    if (!channels) return;

    const space = get().space;
    const keys = space?.id ? [`faq:${space.id}`] : Object.keys(channels);
    for (const key of keys) {
      const channel = channels[key];
      if (channel) {
        supabase.removeChannel(channel);
        delete channels[key];
      }
    }
  },

  dismissToast: () => set({ realtimeToast: null }),
}));
